import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Fab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { format, parseISO } from "date-fns";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { propertyCollection } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import LazyImage from "./LazyImage";
import { useBatchLazyLoading } from "../hooks/useLazyLoading";

function RoomsSingleTimeline({ singleRoom, setSingleRoomTimeline }) {
  const [loading, setLoading] = useState(true);
  const [groupedImages, setGroupedImages] = useState({});
  const [allImages, setAllImages] = useState([]);
  const [error, setError] = useState(null);
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  // Use batch lazy loading for better performance
  const { visibleItems: visibleImages, loadNextBatch, hasMore, isLoadingMore } =
    useBatchLazyLoading(allImages, 20);

  const fetchLatestImages = useCallback(async () => {
    if (!singleRoom?.displayName || !selectedProperty) {
      setError("Missing room or property information");
      setLoading(false);
      return;
    }

    try {
      setError(null);
      // Reference to your collection
      const imagesRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "units",
        singleRoom.displayName,
        "images"
      );

      // Create a query against the collection, ordering by upload timestamp descending
      const q = query(imagesRef, orderBy("uploadedAt", "desc"));

      const querySnapshot = await getDocs(q);
      const images = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore Timestamp to JavaScript Date object for easier handling
        uploadedAt: doc.data().uploadedAt?.toDate() || new Date(),
      }));

      // Store all images for batch loading
      setAllImages(images);

      // Group images by date for display
      const groupedImages = images.reduce((groups, image) => {
        const date = format(image.uploadedAt, "yyyy-MM-dd");
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(image);
        return groups;
      }, {});

      setGroupedImages(groupedImages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching images: ", error);
      setError(`Failed to load images: ${error.message}`);
      setLoading(false);
    }
  }, [singleRoom?.displayName, selectedProperty]);

  useEffect(() => {
    fetchLatestImages();
  }, [fetchLatestImages]);

  // Helper function to group visible images by date
  const getVisibleImagesByDate = () => {
    const visibleGrouped = {};
    visibleImages.forEach(image => {
      const date = format(image.uploadedAt, "yyyy-MM-dd");
      if (!visibleGrouped[date]) {
        visibleGrouped[date] = [];
      }
      visibleGrouped[date].push(image);
    });
    return visibleGrouped;
  };

  const visibleGroupedImages = getVisibleImagesByDate();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading images...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={4}>
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Images
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchLatestImages}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ minWidth: 275 }}>
        <CardHeader
          style={{ background: "#282828", color: "white" }}
          title={
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" component="div">
                {singleRoom?.displayName || 'Room Timeline'}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)">
                ({allImages.length} images)
              </Typography>
            </Box>
          }
          action={
            <Button
              variant="contained"
              onClick={() => setSingleRoomTimeline(false)}
              sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              Back
            </Button>
          }
        />
        <CardContent>
          {Object.keys(visibleGroupedImages).length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Images Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No images have been uploaded for this room yet.
              </Typography>
            </Box>
          ) : (
            <Box>
              {Object.keys(visibleGroupedImages)
                .sort()
                .reverse()
                .map((date) => (
                  <Box key={date} sx={{ mb: 4 }}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        borderBottom: '2px solid #e0e0e0',
                        pb: 1,
                        mb: 2,
                        color: 'primary.main'
                      }}
                    >
                      {format(parseISO(date), "MMMM dd, yyyy")}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({visibleGroupedImages[date].length} images)
                      </Typography>
                    </Typography>

                    <ImageList cols={5} gap={8}>
                      {visibleGroupedImages[date].map((item) => (
                        <ImageListItem key={item.id}>
                          <LazyImage
                            src={`${item.url}?w=164&h=164&fit=crop&auto=format`}
                            srcSet={`${item.url}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                            alt={item.name || `Image from ${format(item.uploadedAt, 'PPP')}`}
                            title={item.name || format(item.uploadedAt, 'PPpp')}
                            downloadUrl={item.url}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                ))}

              {hasMore && (
                <Box display="flex" justifyContent="center" mt={4} mb={2}>
                  <Fab
                    variant="extended"
                    color="primary"
                    onClick={loadNextBatch}
                    disabled={isLoadingMore}
                    sx={{ px: 4 }}
                  >
                    {isLoadingMore ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ mr: 1 }} />
                    )}
                    {isLoadingMore ? 'Loading...' : `Load More Images (${allImages.length - visibleImages.length} remaining)`}
                  </Fab>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default RoomsSingleTimeline;
